import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { dateFmt } from "@/lib/format";
import { fileToDataUrl } from "@/lib/image";
import { PageLoader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Megaphone, Plus, Pencil, Trash2, Loader2, Upload } from "lucide-react";

const empty = () => ({ title: "", content: "", excerpt: "", cover_url: null, published: true });

export default function Marketing() {
  const qc = useQueryClient();
  const { data: posts, isLoading } = useQuery({ queryKey: ["admin-posts"], queryFn: async () => (await api.get("/admin/posts")).data });

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty());

  const openNew = () => { setEditId(null); setForm(empty()); setOpen(true); };
  const openEdit = (p) => { setEditId(p.id); setForm({ title: p.title, content: p.content, excerpt: p.excerpt || "", cover_url: p.cover_url || null, published: p.published }); setOpen(true); };

  const save = useMutation({
    mutationFn: async () => {
      const body = { ...form };
      if (editId) return (await api.put(`/admin/posts/${editId}`, body)).data;
      return (await api.post("/admin/posts", body)).data;
    },
    onSuccess: () => {
      toast.success(editId ? "Post updated" : "Post published");
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["admin-posts"] });
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/posts/${id}`)).data,
    onSuccess: () => { toast.success("Post deleted"); qc.invalidateQueries({ queryKey: ["admin-posts"] }); },
  });

  const valid = form.title.trim() && form.content.trim();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.1em] text-muted-foreground">Marketing blog</p>
          <p className="text-sm text-muted-foreground mt-1">Publish marketing tips and updates for your authors to read.</p>
        </div>
        <Button data-testid="add-post-button" onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> New Post</Button>
      </div>

      {isLoading ? (
        <PageLoader />
      ) : posts.length === 0 ? (
        <EmptyState icon={Megaphone} title="No posts yet" desc="Write your first marketing post for authors." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Card key={p.id} data-testid={`post-card-${p.id}`} className="overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-md transition-transform duration-200">
              {p.cover_url && <img src={p.cover_url} alt={p.title} className="h-36 w-full object-cover" />}
              <div className="p-5 flex-1 flex flex-col">
                <div className="flex items-center gap-2">
                  <Badge variant={p.published ? "secondary" : "outline"} className="font-normal">{p.published ? "Published" : "Draft"}</Badge>
                  <span className="text-xs text-muted-foreground">{dateFmt(p.created_at)}</span>
                </div>
                <p className="font-head font-semibold mt-2 leading-tight line-clamp-2">{p.title}</p>
                <p className="text-sm text-muted-foreground mt-1.5 line-clamp-3 flex-1">{p.excerpt}</p>
                <div className="flex gap-2 mt-4">
                  <Button variant="outline" size="sm" className="flex-1 gap-1.5" data-testid={`edit-post-${p.id}`} onClick={() => openEdit(p)}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-1.5 text-destructive hover:text-destructive" data-testid={`delete-post-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this post?</AlertDialogTitle>
                        <AlertDialogDescription>Authors will no longer see "{p.title}".</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => del.mutate(p.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-head">{editId ? "Edit post" : "New marketing post"}</DialogTitle>
            <DialogDescription>Share marketing content and tips with your authors.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input data-testid="post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="How to promote your launch" />
            </div>
            <div className="space-y-2">
              <Label>Short excerpt (optional)</Label>
              <Input data-testid="post-excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="One-line summary shown on cards" />
            </div>
            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea data-testid="post-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Write your post... use Markdown: # Heading, **bold**, *italic*, [link](https://...), - list" className="min-h-[160px] font-mono text-sm" />
              <p className="text-xs text-muted-foreground">Supports Markdown — <code># Heading</code>, <code>**bold**</code>, <code>*italic*</code>, <code>[text](url)</code>, <code>- bullet</code>.</p>
            </div>
            <div className="space-y-2">
              <Label>Cover image (optional)</Label>
              <div className="flex items-center gap-3">
                {form.cover_url && <img src={form.cover_url} alt="cover" className="h-14 w-24 object-cover rounded-md border border-border" />}
                <input id="post-cover-file" type="file" accept="image/*" className="hidden" data-testid="post-cover-upload"
                  onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const url = await fileToDataUrl(f, 800); setForm((p) => ({ ...p, cover_url: url })); } catch (err) { toast.error(err.message); } e.target.value = ""; }} />
                <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => document.getElementById("post-cover-file").click()}>
                  <Upload className="h-4 w-4" /> {form.cover_url ? "Replace" : "Upload"}
                </Button>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border px-4 py-3">
              <div>
                <Label>Published</Label>
                <p className="text-xs text-muted-foreground">Visible to authors when on.</p>
              </div>
              <Switch data-testid="post-published" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-post-button" onClick={() => save.mutate()} disabled={!valid || save.isPending}>
              {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? "Save changes" : "Publish post"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
